import { useQuery } from "@tanstack/react-query";
import { useFormContext, useWatch } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { FormAutocomplete } from "../../../../common/form-fields-wrappers";
import { ICustomerDataForm } from "../../../../models/models";
import { fetchProvincesByCountryCode } from "../../../services/http.services";
import countryMappings from "./country-mappings";
import { useEffect } from "react";

const DrivingLicenseIssuingProvinceField = ({ referenceProp, disabled }: { referenceProp: string, disabled: boolean }) => {

	const { t } = useTranslation('network');

	const formContext = useFormContext();

	const [
		drivingLicenseIssuingCountry,
		drivingLicenseIssuingProvince,
		drivingLicenseIssuingProvinceName,
		drivingLicenseIssuingPlace,
	] = useWatch({
		control: formContext.control,
		name: [
			`${referenceProp}.drivingLicenseIssuingCountry`,
			`${referenceProp}.drivingLicenseIssuingProvince`,
			`${referenceProp}.drivingLicenseIssuingProvinceName`,
			`${referenceProp}.issuingPlace`,
		]
	})

	const provincesQuery = useQuery({
		queryKey: ['ntw_provinces', drivingLicenseIssuingCountry],
		queryFn: () => {
			return fetchProvincesByCountryCode({ code: drivingLicenseIssuingCountry ?? 'IT' });
		}
	});

	const handleChange = () => {
		if (drivingLicenseIssuingCountry === 'IT') {
			formContext.setValue(`${referenceProp}.issuingPlace`, null);
		}
	}

	useEffect(() => {
		const countryCodeFromPlace = countryMappings[drivingLicenseIssuingPlace?.description];

		const FOREIGN_STATES = {
			country: countryCodeFromPlace,
			codeProvince: "ES",
			description: "Foreign States",
		};

		if (drivingLicenseIssuingCountry === 'IT') {
			if (drivingLicenseIssuingProvince?.country !== 'IT') {
				formContext.setValue(`${referenceProp}.drivingLicenseIssuingProvince`, null);
				formContext.setValue(`${referenceProp}.drivingLicenseIssuingProvinceName`, null);
			}
		} else if (drivingLicenseIssuingCountry === 'FR') {
			if (drivingLicenseIssuingProvince?.country !== 'FR') {
				formContext.setValue(`${referenceProp}.drivingLicenseIssuingProvince`, null);
				formContext.setValue(`${referenceProp}.drivingLicenseIssuingProvinceName`, null);
			}
		} else if (provincesQuery.data && drivingLicenseIssuingProvinceName && drivingLicenseIssuingProvinceName !== FOREIGN_STATES.codeProvince) {
			const prov = provincesQuery.data?.response?.find((p: any) => p.codeProvince === drivingLicenseIssuingProvinceName);
			if (prov) {
				formContext.setValue(`${referenceProp}.drivingLicenseIssuingCountry`, 'IT');
				formContext.setValue(`${referenceProp}.drivingLicenseIssuingProvince`, prov);
				formContext.setValue(`${referenceProp}.drivingLicenseIssuingProvinceName`, prov.codeProvince);
			}
		} else if (!drivingLicenseIssuingCountry?.length) {
			if (drivingLicenseIssuingProvince?.country) {
				formContext.setValue(`${referenceProp}.drivingLicenseIssuingCountry`, drivingLicenseIssuingProvince?.country);
			} else if (drivingLicenseIssuingPlace && countryCodeFromPlace) {
				formContext.setValue(`${referenceProp}.drivingLicenseIssuingCountry`, countryCodeFromPlace);
			}
		} else {
			formContext.setValue(`${referenceProp}.drivingLicenseIssuingProvince`, FOREIGN_STATES);
			formContext.setValue(`${referenceProp}.drivingLicenseIssuingProvinceName`, FOREIGN_STATES.codeProvince);
		}
	}, [
		provincesQuery.data,
		drivingLicenseIssuingCountry, drivingLicenseIssuingProvince?.codeProvince, drivingLicenseIssuingProvinceName
	]);

	return (
		<FormAutocomplete<ICustomerDataForm>
			name={`${referenceProp}.drivingLicenseIssuingProvince`}
			label={t("customerData.form.label.drivingLicenseIssuingProvince")}
			options={provincesQuery.data?.response ?? []}
			isOptionEqualToValue={(option: any, value: any) => option.codeProvince === value.codeProvince}
			getOptionLabel={(option) => option.description}
			fullWidth
			loading={provincesQuery.isInitialLoading}
			onChange={handleChange}
			disabled={disabled}
		/>
	)
}

export default DrivingLicenseIssuingProvinceField
